import { Fragment } from "react";
import { UI } from "../constants/tokens";

// ─── Academy text · the few Markdown marks the content uses ──────────────────
// **bold**, bare links, and Latin runs. A Latin run (a ticker, a form name, an
// English term) sits in <bdi dir="auto"> so it keeps its own direction inside
// a Hebrew sentence. Nothing is injected as HTML.
const TOKEN_RE = /(\*\*[^*]+\*\*|https?:\/\/[^\s)]+)/g;
// One Latin run, as one LTR isolate (sprint 14, 3.4). Three cases the old
// pattern split, so that the bidi algorithm reordered the pieces:
//   · a form name that starts with a digit · "10-K" came out "K-10", since
//     only the "K" was in the isolate
//   · a list of acronyms · "SEC, FINRA" is one run, not two runs and a comma
//     that RTL placed on the wrong side. Only acronyms: "לפי Investor.gov,
//     EDGAR נותנת" is two clauses, not a list (review)
//   · an English term in parentheses · "(common stock)" with its brackets,
//     which otherwise could pair across the isolate and face outward
// A dot belongs to a word only between letters ("Investor.gov"): the full
// stop of "תחת פיקוח ה-SEC." stays outside, on the sentence's side, and a run
// never crosses into the next sentence ("SEC. SIPC"). A few abbreviations
// keep their dots ("S&P U.S. Indices").
// An index whose name ends in a number is one word ("S&P 500"); a bare
// number after any other term does not join it · in "AAPL 3 מניות" the 3
// belongs to the Hebrew.
const WORD = "(?:U\\.S\\.|Inc\\.|Corp\\.|Ltd\\.|(?:S&P|Russell|Nasdaq|NASDAQ|FTSE|Nikkei|DAX|Dow Jones|Dow|TA) \\d+|\\d+\\([a-z]\\)|\\d+[-‑]?[A-Za-z][A-Za-z0-9]*|[A-Za-z][A-Za-z0-9&'’/+-]*(?:\\.[A-Za-z0-9]+)*)(?:\\(\\w\\))?";
const ACRONYM = "[A-Z][A-Z0-9&]*[A-Z0-9](?![A-Za-z0-9&])";
const RUN = `(?:${ACRONYM}(?:, ${ACRONYM})+|${WORD})(?: ${WORD})*`;
// A name quoted in full, all of it Latin, is one run with its quotes: the
// FRED series "Crude Oil Prices: West Texas Intermediate (WTI) - Cushing,
// Oklahoma" came apart at the colon, the dash and the comma (David,
// 06/10/2026). No Hebrew letter may sit inside the quotes.
const QUOTED = '"[^"\\u0590-\\u05FF]*[A-Za-z][^"\\u0590-\\u05FF]*"';
const LATIN_RE = new RegExp(`(${QUOTED}|\\(${RUN}(?:, ${RUN})*\\)|${RUN})`, "g");

function latin(text, keyBase) {
  const parts = String(text).split(LATIN_RE);
  return parts.map((p, i) => (i % 2 === 1
    ? <bdi key={`${keyBase}-${i}`} dir="ltr">{p}</bdi>
    : <Fragment key={`${keyBase}-${i}`}>{p}</Fragment>));
}

export function Link({ href, children }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer"
      style={{ color: UI.interactive, textDecoration: "underline", textUnderlineOffset: 3, overflowWrap: "anywhere" }}>
      <bdi dir="auto">{children}</bdi>
    </a>
  );
}

export function Rich({ text }) {
  const parts = String(text).split(TOKEN_RE);
  return parts.map((p, i) => {
    if (!p) return null;
    if (p.startsWith("**") && p.endsWith("**")) return <strong key={i}>{latin(p.slice(2, -2), i)}</strong>;
    if (/^https?:\/\//.test(p)) {
      const href = p.replace(/[.,;]+$/, "");
      return <Link key={i} href={href}>{href}</Link>;
    }
    return <Fragment key={i}>{latin(p, i)}</Fragment>;
  });
}

// A list of sources. Each source is its description and its links; a link
// opens in a new tab. A source with no link (the app itself) is text only.
export function Sources({ sources, title = "מקורות", checked }) {
  if (!sources || !sources.length) return null;
  return (
    <div>
      <div style={{ fontSize: 13.5, fontWeight: 700, color: UI.text.primary, marginBottom: 6 }}>
        {title}{checked ? <span style={{ fontWeight: 500, color: UI.text.muted }}> · נבדק <bdi dir="ltr">{checked}</bdi></span> : null}
      </div>
      <ul style={{ margin: 0, paddingInlineStart: 18, display: "flex", flexDirection: "column", gap: 6 }}>
        {sources.map((s, i) => (
          <li key={i} style={{ fontSize: 13, color: UI.text.secondary, lineHeight: 1.6 }}>
            {/* The whole line takes the direction of its first strong letter:
                "Investor.gov (SEC) · Diversification" reads left to right,
                "רשות המסים · טופס 1322" right to left. Split into separate
                Latin runs, an English line came out in reverse order. */}
            <bdi dir="auto">{s.text}</bdi>
            {s.urls.map((u, j) => (
              <div key={j}><Link href={u}>{u.replace(/^https?:\/\//, "").replace(/\/$/, "")}</Link></div>
            ))}
          </li>
        ))}
      </ul>
    </div>
  );
}
